/**
 * Openclaw Community API Client
 *
 * Phase 1: Read-only endpoints call real backend via apiFetch + transforms.
 * Functions that have no backend counterpart still use mock data.
 * Function signatures are unchanged — components need no modification.
 */

import { apiFetch } from "@/lib/api/client";
import type {
  AbilityType,
  AbilityGroup,
  Badge,
  ActivityEvent,
  CommunityBoard,
  CommunityComment,
  CommunityFeedResponse,
  CommunityPost,
  CommunityUser,
  CreatorEarnings,
  FeedTab,
  PublishDraft,
} from "./types";
import type {
  ApiPostListResp,
  ApiPostDetailResp,
  ApiCommentListResp,
  ApiSectionListResp,
  ApiUserDetailResp,
  ApiFollowListResp,
  ApiSearchResp,
  ApiPostCardItem,
  ApiCommentItem,
  ApiCreatePostReq,
  ApiRankingCreatorsResp,
  ApiRankingPostsResp,
  ApiHotTagsResp,
  ApiFavoriteListResp,
  ApiSectionCategoryListResp,
  ApiSubCommentListResp,
  ApiProductDetailResp,
  ApiOrderPayCombinedReq,
  ApiOrderPayCombinedResp,
  PayWay,
} from "./community-types-api";
import {
  transformPostCard,
  transformPostDetail,
  transformComment,
  transformSections,
  transformUser,
  mapAbilityType,
} from "./community-transforms";
import { slugToSectionId } from "./boards";

// ── Discourse API base path ──

const D = "/discourse/api/v1";

// ── Helper: convert ApiPostListResp → CommunityFeedResponse ──

function toFeedResponse(
  resp: ApiPostListResp,
  page: number,
  pageSize: number,
  filterTag?: string,
): CommunityFeedResponse {
  let posts = resp.list.map((card) => transformPostCard(card));
  // Client-side tag filtering (backend doesn't support tag-based queries)
  if (filterTag) {
    posts = posts.filter((p) =>
      p.tags.some((t) => t.toLowerCase() === filterTag.toLowerCase()),
    );
  }
  return {
    posts,
    total: filterTag ? posts.length : resp.total,
    page,
    pageSize,
    hasMore: filterTag ? false : page * pageSize < resp.total,
  };
}

/* ══════════════════════════════════════════
   Feed
   ══════════════════════════════════════════ */

export async function fetchCommunityFeed(params: {
  tab?: FeedTab;
  boardSlug?: string;
  abilityType?: AbilityType;
  search?: string;
  tag?: string;
  page?: number;
  pageSize?: number;
  sort?: "hot" | "new" | "top" | "likes";
  sortDirection?: "desc" | "asc";
}): Promise<CommunityFeedResponse> {
  const {
    boardSlug,
    abilityType,
    search,
    tag,
    tab,
    page = 1,
    pageSize = 12,
  } = params;

  // Search uses a dedicated endpoint
  if (search) {
    const resp = await apiFetch<ApiSearchResp>(`${D}/search`, {
      params: {
        keyword: search,
        type: "post",
        page,
        pageSize,
      },
    });
    // Backend may return results in `posts` or in `items[].post` — merge both
    const directPosts = resp.posts ?? [];
    const itemPosts = (resp.items ?? [])
      .filter((item) => item.post != null)
      .map((item) => item.post!);
    const seenIds = new Set<string>();
    const mergedCards = [...directPosts, ...itemPosts].filter((card) => {
      const id = String(card.post?.id ?? card.post);
      if (seenIds.has(id)) return false;
      seenIds.add(id);
      return true;
    });
    const posts = mergedCards.map((card) => transformPostCard(card));
    const total = resp.total || posts.length;
    return {
      posts,
      total,
      page,
      pageSize,
      hasMore: page * pageSize < total,
    };
  }

  // Tab-based routing
  if (tab === "following") {
    const resp = await apiFetch<ApiPostListResp>(`${D}/post/following`, {
      params: {
        page,
        pageSize,
        ...(boardSlug && { sectionId: slugToSectionId(boardSlug) }),
        ...(abilityType && { postType: mapAbilityType(abilityType) }),
      },
    });
    return toFeedResponse(resp, page, pageSize, tag);
  }

  if (tab === "hot") {
    const resp = await apiFetch<ApiPostListResp>(`${D}/post/hot`, {
      params: {
        page,
        pageSize,
        ...(boardSlug && { sectionId: slugToSectionId(boardSlug) }),
        ...(abilityType && { postType: mapAbilityType(abilityType) }),
      },
    });
    return toFeedResponse(resp, page, pageSize, tag);
  }

  // Default: /post/list with optional filters
  const queryParams: Record<string, string | number | undefined> = {
    page,
    pageSize,
  };

  if (boardSlug) {
    queryParams.sectionId = slugToSectionId(boardSlug);
  }
  if (abilityType) {
    queryParams.postType = mapAbilityType(abilityType);
  }

  // "recommended" tab uses the recommend endpoint
  if (tab === "recommended") {
    const resp = await apiFetch<ApiPostListResp>(`${D}/post/recommend`, {
      params: {
        page,
        pageSize,
        ...(boardSlug && { sectionId: slugToSectionId(boardSlug) }),
        ...(abilityType && { postType: mapAbilityType(abilityType) }),
      },
    });
    return toFeedResponse(resp, page, pageSize, tag);
  }

  const resp = await apiFetch<ApiPostListResp>(`${D}/post/list`, {
    params: queryParams,
  });
  return toFeedResponse(resp, page, pageSize, tag);
}

/* ══════════════════════════════════════════
   Single Post
   ══════════════════════════════════════════ */

export async function fetchPost(id: string): Promise<CommunityPost | null> {
  try {
    const raw = await apiFetch<Record<string, unknown>>(`${D}/post/${id}`);
    // Normalize: detail API may return flat object or nested {post, author, tags}
    const resp: ApiPostDetailResp = (raw as ApiPostDetailResp).post
      ? (raw as ApiPostDetailResp)
      : {
          post: raw as unknown as ApiPostItem,
          author: (raw as Record<string, unknown>).author as ApiUserInfo,
          tags: ((raw as Record<string, unknown>).tags ?? []) as ApiTagItem[],
        };
    return transformPostDetail(resp);
  } catch {
    return null;
  }
}

/**
 * 上报帖子浏览记录（埋点）。
 * 调用 GET /post/{id} 让后端记录浏览行为，供推荐算法使用。
 * 去重由后端处理，失败静默忽略。
 */
export async function reportPostView(id: string): Promise<void> {
  try {
    await apiFetch(`${D}/post/${id}`);
  } catch {
    // 埋点失败不影响用户体验
  }
}

/* ══════════════════════════════════════════
   Comments
   ══════════════════════════════════════════ */

export async function fetchComments(
  postId: string,
  page = 1,
): Promise<{ comments: CommunityComment[]; total: number; hasMore: boolean }> {
  const pageSize = 20;
  const resp = await apiFetch<ApiCommentListResp>(`${D}/comment/list`, {
    params: { postId, page, pageSize },
  });
  return {
    comments: resp.list.map((item) => transformComment(item)),
    total: resp.total,
    hasMore: page * pageSize < resp.total,
  };
}

/**
 * 获取某一顶级评论下的全部子评论（展开“共N条回复”时调用）
 */
export async function fetchReplies(
  parentId: string,
  postId: string,
  parentAuthorName: string,
  page = 1,
  pageSize = 20,
): Promise<{ replies: CommunityComment[]; total: number }> {
  const resp = await apiFetch<ApiSubCommentListResp>(`${D}/comment/replies`, {
    params: { parentId: Number(parentId), page, pageSize },
  });
  return {
    replies: resp.list.map((item) =>
      transformComment(item, postId, parentAuthorName),
    ),
    total: resp.total,
  };
}

/* ══════════════════════════════════════════
   Interactions
   ══════════════════════════════════════════ */

export async function togglePostLike(
  postId: string,
  currentlyLiked?: boolean,
): Promise<{ liked: boolean; count: number }> {
  const shouldUnlike = currentlyLiked ?? false;
  if (shouldUnlike) {
    await apiFetch(`${D}/like`, {
      method: "DELETE",
      body: { targetId: Number(postId), targetType: "post" },
    });
    return { liked: false, count: -1 }; // count is relative delta; caller handles optimistic UI
  } else {
    await apiFetch(`${D}/like`, {
      method: "POST",
      body: { targetId: Number(postId), targetType: "post" },
    });
    return { liked: true, count: 1 };
  }
}

export async function togglePostFavorite(
  postId: string,
  currentlyFavorited?: boolean,
): Promise<{ favorited: boolean }> {
  if (currentlyFavorited) {
    await apiFetch(`${D}/favorite/${postId}`, { method: "DELETE" });
    return { favorited: false };
  } else {
    await apiFetch(`${D}/favorite/${postId}`, { method: "POST" });
    return { favorited: true };
  }
}

export async function toggleCommentLike(
  commentId: string,
  currentlyLiked: boolean,
): Promise<void> {
  if (currentlyLiked) {
    await apiFetch(`${D}/like`, {
      method: "DELETE",
      body: { targetId: Number(commentId), targetType: "comment" },
    });
  } else {
    await apiFetch(`${D}/like`, {
      method: "POST",
      body: { targetId: Number(commentId), targetType: "comment" },
    });
  }
}

export async function followUser(userId: string): Promise<void> {
  await apiFetch(`${D}/follow/${userId}`, { method: "POST" });
}

export async function unfollowUser(userId: string): Promise<void> {
  await apiFetch(`${D}/follow/${userId}`, { method: "DELETE" });
}

export async function copyAbilityToAccount(
  _postId: string,
): Promise<{ success: boolean; message: string }> {
  // No backend endpoint — keep mock
  const messages = [
    "🎉 恭喜，能力 +1",
    "✅ 收入囊中！去「我的空间」查看",
    "🦞 Claw里多了一样新东西——好好用，别浪费前人的心血",
  ];
  return {
    success: true,
    message: messages[Math.floor(Math.random() * messages.length)],
  };
}

export async function tipCreator(
  _postId: string,
  _amount: number,
): Promise<{ success: boolean; message: string }> {
  // No backend endpoint — keep mock
  return {
    success: true,
    message: "🪙 你的赏钱已送达，一杯咖啡的分量，可能就是对方继续更新的动力",
  };
}

export async function deployTeam(
  _postId: string,
): Promise<{ success: boolean; message: string }> {
  // No backend endpoint — keep mock
  return {
    success: true,
    message: "🚀 团队已部署到你的工作空间，前往「我的空间」开始协作",
  };
}

/* ══════════════════════════════════════════
   Publish / Write
   ══════════════════════════════════════════ */

export async function publishPost(draft: PublishDraft): Promise<CommunityPost> {
  const body: ApiCreatePostReq = {
    title: draft.title,
    content: draft.description,
    coverImage: draft.coverImage,
    sectionId: Number(slugToSectionId(draft.boardSlug)),
    postType: mapAbilityType(draft.abilityType),
    tagNames: draft.tags,
    aiMessage: draft.aiMessage,
    skillIds: draft.skillIds,
  };
  const resp = await apiFetch<ApiPostDetailResp>(`${D}/post`, {
    method: "POST",
    body,
  });
  return transformPostDetail(resp);
}

export async function updatePost(
  id: string,
  data: {
    title?: string;
    content?: string;
    coverImage?: string;
    boardSlug?: string;
    tags?: string[];
  },
): Promise<void> {
  await apiFetch(`${D}/post/${id}`, {
    method: "PUT",
    body: {
      title: data.title,
      content: data.content,
      coverImage: data.coverImage,
      sectionId: data.boardSlug
        ? Number(slugToSectionId(data.boardSlug))
        : undefined,
      tagNames: data.tags,
    },
  });
}

export async function deletePost(id: string): Promise<void> {
  await apiFetch(`${D}/post/${id}`, { method: "DELETE" });
}

export async function createComment(
  postId: string,
  content: string,
  parentId?: string,
): Promise<CommunityComment> {
  const resp = await apiFetch<ApiCommentItem>(`${D}/comment`, {
    method: "POST",
    body: {
      postId: Number(postId),
      content,
      parentId: parentId ? Number(parentId) : undefined,
    },
  });
  return transformComment(resp);
}

export async function deleteComment(id: string): Promise<void> {
  await apiFetch(`${D}/comment/${id}`, { method: "DELETE" });
}

/* ══════════════════════════════════════════
   User Profile
   ══════════════════════════════════════════ */

export async function fetchUserProfile(
  userId: string,
): Promise<CommunityUser | null> {
  try {
    if (userId === "u-self") {
      // Current user profile
      const resp = await apiFetch<ApiUserDetailResp>(`${D}/user/profile`);
      return transformUser(resp.userInfo, resp.stats);
    }
    const resp = await apiFetch<ApiUserDetailResp>(`${D}/user/${userId}`);
    return transformUser(resp.userInfo, resp.stats, {
      isFollowed: resp.isFollowed,
    });
  } catch {
    // Fallback: build profile from stored login data
    if (userId === "u-self") {
      const { getStoredUser } = await import("@/lib/api/client");
      const { generateAvatarDataUri, getDisplayName } =
        await import("@/lib/avatar");
      const stored = getStoredUser();
      const displayName = stored
        ? getDisplayName({
            username: stored.username,
            email: stored.email,
            phone: stored.phone,
          })
        : "用户";
      const avatarSeed =
        stored?.email || stored?.username || stored?.phone || "user";
      return {
        id: "u-self",
        displayName,
        handle: stored?.username || undefined,
        avatar: generateAvatarDataUri(avatarSeed),
        bio: "",
        isVerified: false,
        creatorLevel: 1,
        joinedAt: new Date().toISOString(),
        stats: {
          posts: 0,
          followers: 0,
          following: 0,
          totalLikes: 0,
          totalCopies: 0,
          totalTips: 0,
        },
      };
    }
    return null;
  }
}

// Cache the resolved "u-self" → real user ID to avoid redundant profile fetches
// (the hot-sort algorithm fetches multiple pages in sequence).
let _cachedSelfId: string | null = null;

export function clearCommunitySelfCache(): void {
  _cachedSelfId = null;
}

export async function fetchUserPosts(
  userId: string,
  page = 1,
): Promise<CommunityFeedResponse> {
  const pageSize = 12;

  // "u-self" is a virtual ID only known to the frontend.
  // The backend /post/user/{userId} requires the real numeric user ID.
  let resolvedId = userId;
  if (userId === "u-self") {
    if (!_cachedSelfId) {
      const profile = await fetchUserProfile("u-self");
      _cachedSelfId = profile?.id ?? null;
    }
    resolvedId = _cachedSelfId ?? userId;
  }

  const resp = await apiFetch<ApiPostListResp>(`${D}/post/user/${resolvedId}`, {
    params: { page, pageSize },
    auth: false,
  });
  return toFeedResponse(resp, page, pageSize);
}

/* ══════════════════════════════════════════
   Boards
   ══════════════════════════════════════════ */

export async function fetchBoards(): Promise<CommunityBoard[]> {
  try {
    const resp = await apiFetch<ApiSectionListResp>(`${D}/section/list`, {
      auth: false,
    });
    return transformSections(resp.groups);
  } catch {
    // Fallback to static boards
    const { COMMUNITY_BOARDS } = await import("./boards");
    return COMMUNITY_BOARDS;
  }
}

/* ══════════════════════════════════════════
   Badges (no backend — mock)
   ══════════════════════════════════════════ */

export async function fetchUserBadges(userId: string): Promise<Badge[]> {
  const { getMockBadgesForUser } = await import("./mock-data");
  return getMockBadgesForUser(userId);
}

export async function fetchAllBadgesWithStatus(
  userId: string,
): Promise<(Badge & { unlocked: boolean })[]> {
  const { getMockAllBadgesWithStatus } = await import("./mock-data");
  return getMockAllBadgesWithStatus(userId);
}

/* ══════════════════════════════════════════
   Activity Events (no backend — mock)
   ══════════════════════════════════════════ */

export async function fetchUserActivity(
  userId: string,
  page = 1,
): Promise<{ events: ActivityEvent[]; hasMore: boolean }> {
  const { getMockActivityForUser } = await import("./mock-data");
  const all = getMockActivityForUser(userId);
  const pageSize = 15;
  const start = (page - 1) * pageSize;
  return {
    events: all.slice(start, start + pageSize),
    hasMore: start + pageSize < all.length,
  };
}

/* ══════════════════════════════════════════
   Ability Groups / Favorites (no backend — mock)
   ══════════════════════════════════════════ */

export async function fetchUserAbilityGroups(
  userId: string,
): Promise<AbilityGroup[]> {
  const { getMockAbilityGroups } = await import("./mock-data");
  return getMockAbilityGroups(userId);
}

export async function fetchMyAbilities(
  userId: string,
): Promise<CommunityPost[]> {
  const { getMockAbilityGroups } = await import("./mock-data");
  const groups = getMockAbilityGroups(userId);
  const seen = new Set<string>();
  const result: CommunityPost[] = [];
  for (const g of groups) {
    for (const item of g.items) {
      if (!seen.has(item.id)) {
        seen.add(item.id);
        result.push(item);
      }
    }
  }
  return result;
}

export async function fetchUserFavorites(
  userId: string,
  groupId?: string,
  page = 1,
): Promise<CommunityFeedResponse> {
  const { getMockAbilityGroups } = await import("./mock-data");
  const groups = getMockAbilityGroups(userId);
  const group = groupId ? groups.find((g) => g.id === groupId) : groups[0];
  const items = group?.items ?? [];
  const pageSize = 12;
  const start = (page - 1) * pageSize;
  return {
    posts: items.slice(start, start + pageSize),
    total: items.length,
    page,
    pageSize,
    hasMore: start + pageSize < items.length,
  };
}

/* ══════════════════════════════════════════
   Followers / Following
   ══════════════════════════════════════════ */

export async function fetchUserFollowers(
  _userId: string,
  page = 1,
): Promise<{ users: CommunityUser[]; total: number; hasMore: boolean }> {
  const pageSize = 20;
  try {
    const resp = await apiFetch<ApiFollowListResp>(`${D}/follow/followers`, {
      params: { page, pageSize },
    });
    return {
      users: resp.list.map((u) => transformUser(u)),
      total: resp.total,
      hasMore: page * pageSize < resp.total,
    };
  } catch {
    return { users: [], total: 0, hasMore: false };
  }
}

export async function fetchUserFollowing(
  _userId: string,
  page = 1,
): Promise<{ users: CommunityUser[]; total: number; hasMore: boolean }> {
  const pageSize = 20;
  try {
    const resp = await apiFetch<ApiFollowListResp>(`${D}/follow/following`, {
      params: { page, pageSize },
    });
    return {
      users: resp.list.map((u) => transformUser(u)),
      total: resp.total,
      hasMore: page * pageSize < resp.total,
    };
  } catch {
    return { users: [], total: 0, hasMore: false };
  }
}

/* ══════════════════════════════════════════
   Creator Earnings (no backend — mock)
   ══════════════════════════════════════════ */

export async function fetchCreatorEarnings(
  userId: string,
): Promise<CreatorEarnings | null> {
  if (userId === "u-self") {
    return {
      totalTipIncome: 0,
      totalPaidWorkflowIncome: 0,
      monthlyTipIncome: 0,
      monthlyPaidIncome: 0,
      withdrawableBalance: 0,
      monthOverMonthChange: 0,
      topWorks: [],
      trendData: [],
    };
  }
  const { getMockEarnings } = await import("./mock-data");
  return getMockEarnings(userId);
}

/* ══════════════════════════════════════════
   User Posts by Type
   ══════════════════════════════════════════ */

export async function fetchUserPostsByType(
  userId: string,
  abilityType: AbilityType,
  page = 1,
): Promise<CommunityFeedResponse> {
  const pageSize = 12;
  const resp = await apiFetch<ApiPostListResp>(`${D}/post/user/${userId}`, {
    params: {
      page,
      pageSize,
      postType: mapAbilityType(abilityType),
    },
    auth: false,
  });
  return toFeedResponse(resp, page, pageSize);
}

/* ══════════════════════════════════════════
   Search & Rankings
   ══════════════════════════════════════════ */

export async function searchCommunity(
  keyword: string,
  type?: "post" | "user" | "tag" | "comment" | "all",
  page = 1,
  pageSize = 20,
): Promise<ApiSearchResp> {
  return apiFetch<ApiSearchResp>(`${D}/search`, {
    params: { keyword, type, page, pageSize },
    auth: false,
  });
}

export async function fetchRankingCreators(): Promise<ApiRankingCreatorsResp> {
  return apiFetch<ApiRankingCreatorsResp>(`${D}/ranking/creators`, {
    auth: false,
  });
}

/** Fetch recommended creators as CommunityUser[] (convenience wrapper) */
export async function fetchRecommendedCreators(): Promise<CommunityUser[]> {
  const resp = await fetchRankingCreators();
  return resp.list.map((item) => transformUser(item.userInfo));
}

export async function fetchRankingPosts(): Promise<CommunityPost[]> {
  const resp = await apiFetch<ApiRankingPostsResp>(`${D}/ranking/posts`, {
    auth: false,
  });
  return resp.list.map((card) => transformPostCard(card));
}

export async function fetchHotTags(): Promise<
  { id: string; name: string; useCount: number }[]
> {
  const resp = await apiFetch<ApiHotTagsResp>(`${D}/tag/hot`, { auth: false });
  return resp.list.map((t) => ({
    id: String(t.id),
    name: t.name,
    useCount: t.useCount,
  }));
}

export async function fetchSectionCategories(): Promise<
  { id: number; name: string; sortOrder: number }[]
> {
  const resp = await apiFetch<ApiSectionCategoryListResp>(
    `${D}/section/categories`,
    { auth: false },
  );
  return resp.categories;
}

export async function fetchFavoriteList(
  page = 1,
  pageSize = 12,
): Promise<CommunityFeedResponse> {
  const resp = await apiFetch<ApiFavoriteListResp>(`${D}/favorite/list`, {
    params: { page, pageSize },
  });
  return {
    posts: resp.list.map((card) => transformPostCard(card)),
    total: resp.total,
    page,
    pageSize,
    hasMore: page * pageSize < resp.total,
  };
}

/* ══════════════════════════════════════════
   Notifications
   ══════════════════════════════════════════ */

export type NotificationType =
  | "like"
  | "comment"
  | "reply"
  | "follow"
  | "favorite"
  | "system"
  | "mention";

export type NotificationTargetType = "post" | "comment";

/** 后端 API 原始返回的通知发送者 */
interface ApiNotificationSender {
  id: string;
  nick: string;
  avatar: string;
  handle: string;
  badge: string;
}

/** 后端 API 原始返回的单条通知 */
interface ApiNotificationRaw {
  id: number;
  type: NotificationType;
  sender: ApiNotificationSender;
  title: string;
  content: string;
  targetId: number;
  targetType: NotificationTargetType;
  isRead: boolean;
  createdAt: string;
}

/** 后端 API 原始返回的通知列表 */
interface ApiNotificationListRaw {
  list: ApiNotificationRaw[];
  total: number;
}

/** 前端使用的通知数据 */
export interface NotificationItem {
  id: number;
  type: NotificationType;
  title: string;
  content: string;
  isRead: boolean;
  createdAt: string;
  targetId: number;
  targetType: NotificationTargetType;
  senderNick: string;
  senderAvatar: string;
  senderHandle: string;
  senderBadge: string;
  senderId: string;
}

export interface NotificationListResp {
  list: NotificationItem[];
  total: number;
}

function mapNotification(raw: ApiNotificationRaw): NotificationItem {
  return {
    id: raw.id,
    type: raw.type,
    title: raw.title,
    content: raw.content,
    isRead: raw.isRead,
    createdAt: raw.createdAt,
    targetId: raw.targetId,
    targetType: raw.targetType,
    senderNick: raw.sender.nick || raw.sender.handle,
    senderAvatar: raw.sender.avatar,
    senderHandle: raw.sender.handle,
    senderBadge: raw.sender.badge,
    senderId: raw.sender.id,
  };
}

export async function fetchNotifications(
  page = 1,
  pageSize = 20,
): Promise<NotificationListResp> {
  const raw = await apiFetch<ApiNotificationListRaw>(`${D}/notification/list`, {
    params: { page, pageSize },
  });
  return {
    list: raw.list.map(mapNotification),
    total: raw.total,
  };
}

export async function markNotificationRead(id: number): Promise<void> {
  await apiFetch(`${D}/notification/${id}/read`, {
    method: "PUT",
  });
}

export async function markAllNotificationsRead(): Promise<void> {
  await apiFetch(`${D}/notification/read-all`, {
    method: "PUT",
  });
}

/* ══════════════════════════════════════════
   Product
   ══════════════════════════════════════════ */

export async function fetchProductDetail(
  id: number | string,
): Promise<ApiProductDetailResp> {
  return apiFetch<ApiProductDetailResp>(`${D}/product/${id}`);
}

/* ══════════════════════════════════════════
   Order
   ══════════════════════════════════════════ */

/** 下单并发起支付（合并接口） */
export async function orderPay(
  req: ApiOrderPayCombinedReq,
): Promise<ApiOrderPayCombinedResp> {
  return apiFetch<ApiOrderPayCombinedResp>(`${D}/order/pay`, {
    method: "POST",
    body: req,
  });
}

export async function cancelOrder(
  orderNo: string,
  reason?: string,
): Promise<void> {
  await apiFetch(`${D}/order/${orderNo}/cancel`, {
    method: "PUT",
    body: { reason: reason ?? "用户取消" },
  });
}

export interface PayStatusResp {
  orderNo: string;
  /** 支付状态：1=待支付 2=已支付 3=已关闭 */
  payStatus: number;
}

export async function fetchPayStatus(orderNo: string): Promise<PayStatusResp> {
  return apiFetch<PayStatusResp>(`${D}/order/${orderNo}/pay-status`);
}

export type { PayWay, ApiOrderPayCombinedResp };
